import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronUp, Instagram, Mail, MapPin, Sparkles, Zap, TrendingUp, Shield, Rocket, Star, Play, ExternalLink, Monitor, Package, Users, ShoppingCart, CheckCircle, ArrowRight, Clock, Award, Headphones, Globe, Target, BarChart3, Truck, CreditCard, Send, Heart, BadgeCheck, Timer, Gift, Crown, Gem, Phone, IndianRupee, Calculator, ChevronLeft, ChevronRight as ChevronRightIcon, Quote, Verified, Lock, RefreshCw, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import LoginModal from '../components/LoginModal';
import WhatsAppButton from '../components/WhatsAppButton';
import LiveSuccessMetrics from '../components/LiveSuccessMetrics';
import OfferBanner from '../components/OfferBanner';
import { fbTrackLead } from '../lib/fbpixel';
import ActivationModal from '../components/ActivationModal';
import { WhatYouGet, PlanEarnings, WebsiteShowcase, TrustBar } from '../components/ActivationSections';
import { SeatsBar, SeatsBadge } from '../components/SeatsUrgency';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animated Wave Background
const WaveBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Gradient Blobs */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-gradient-to-r from-blue-400/20 to-cyan-400/20 rounded-full blur-[100px] animate-blob" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-gradient-to-r from-blue-500/15 to-blue-600/15 rounded-full blur-[100px] animate-blob animation-delay-2000" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-to-r from-cyan-400/10 to-blue-400/10 rounded-full blur-[120px] animate-blob animation-delay-4000" />
      
      {/* Floating Elements */}
      {[...Array(15)].map((_, i) => (
        <div
          key={i}
          className="absolute w-3 h-3 rounded-full bg-blue-500/20"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float ${10 + Math.random() * 10}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`
          }}
        />
      ))}
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      
      {/* Wave SVG */}
      <svg className="absolute bottom-0 left-0 right-0 w-full" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 120L60 105C120 90 240 60 360 55C480 50 600 70 720 75C840 80 960 70 1080 60C1200 50 1320 40 1380 35L1440 30V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
      </svg>
    </div>
  );
};

// Live Order Notification Component - Removed as per request
// Keeping component structure for potential future use

// Earnings Calculator Component
const EarningsCalculator = () => {
  const [ordersPerDay, setOrdersPerDay] = useState(10);
  const [avgProfit, setAvgProfit] = useState(300);
  
  const dailyEarnings = ordersPerDay * avgProfit;
  const monthlyEarnings = dailyEarnings * 30;
  const yearlyEarnings = monthlyEarnings * 12;

  return (
    <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0 text-white overflow-hidden relative">
      <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      <CardContent className="p-4 sm:p-6 md:p-8 relative z-10">
        <div className="flex items-center space-x-2 mb-5 sm:mb-6">
          <Calculator className="w-5 h-5 sm:w-6 sm:h-6" />
          <h3 className="text-lg sm:text-xl font-bold">Earnings Calculator</h3>
        </div>
        
        <div className="space-y-5 sm:space-y-6">
          <div>
            <label className="text-xs sm:text-sm text-blue-100 mb-2 block">Orders per day: <span className="font-bold text-white">{ordersPerDay}</span></label>
            <input
              type="range"
              min="5"
              max="50"
              value={ordersPerDay}
              onChange={(e) => setOrdersPerDay(Number(e.target.value))}
              className="w-full h-2 bg-blue-400 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-[10px] sm:text-xs text-blue-200 mt-1">
              <span>5</span>
              <span>50</span>
            </div>
          </div>
          
          <div>
            <label className="text-xs sm:text-sm text-blue-100 mb-2 block">Avg. profit per order: <span className="font-bold text-white">₹{avgProfit}</span></label>
            <input
              type="range"
              min="100"
              max="1000"
              step="50"
              value={avgProfit}
              onChange={(e) => setAvgProfit(Number(e.target.value))}
              className="w-full h-2 bg-blue-400 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-[10px] sm:text-xs text-blue-200 mt-1">
              <span>₹100</span>
              <span>₹1000</span>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-2 sm:gap-3 pt-4 border-t border-blue-500">
            <div className="text-center min-w-0">
              <p className="text-base sm:text-2xl md:text-3xl font-bold whitespace-nowrap tracking-tight">₹{dailyEarnings.toLocaleString()}</p>
              <p className="text-[10px] sm:text-xs text-blue-200 mt-0.5">Daily</p>
            </div>
            <div className="text-center min-w-0 border-x border-blue-500/40">
              <p className="text-base sm:text-2xl md:text-3xl font-bold text-yellow-300 whitespace-nowrap tracking-tight">₹{(monthlyEarnings/1000).toFixed(0)}K</p>
              <p className="text-[10px] sm:text-xs text-blue-200 mt-0.5">Monthly</p>
            </div>
            <div className="text-center min-w-0">
              <p className="text-base sm:text-2xl md:text-3xl font-bold whitespace-nowrap tracking-tight">₹{(yearlyEarnings/100000).toFixed(1)}L</p>
              <p className="text-[10px] sm:text-xs text-blue-200 mt-0.5">Yearly</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [isVisible, end, duration]);

  return <span ref={countRef}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// Scroll Animation Hook
const useScrollAnimation = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1, rootMargin: '50px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// Animated Section Wrapper
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Typing Effect Component
const TypingEffect = ({ texts, className = '' }) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const text = texts[currentTextIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setCurrentText(text.substring(0, currentText.length + 1));
        if (currentText === text) {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        setCurrentText(text.substring(0, currentText.length - 1));
        if (currentText === '') {
          setIsDeleting(false);
          setCurrentTextIndex((prev) => (prev + 1) % texts.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentTextIndex, texts]);

  return (
    <span className={className}>
      {currentText}
      <span className="animate-pulse">|</span>
    </span>
  );
};

// Reels Carousel — auto-scrolling (right-to-left) short video reels, admin managed
const ReelsCarousel = ({ reels, onOpen }) => {
  if (!reels || reels.length === 0) return null;
  const loop = reels.length < 4 ? [...reels, ...reels, ...reels, ...reels] : [...reels, ...reels];
  return (
    <section id="reels" className="py-14 md:py-20 px-0 bg-white overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-2 bg-pink-100 border border-pink-200 rounded-full text-pink-600 text-sm font-medium mb-4">🎬 Watch Real Reels</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-3">Real Stores. <span className="text-blue-600">Real Results.</span></h2>
          <p className="text-slate-600 max-w-2xl mx-auto">Quick reels from our clients&apos; winning stores &amp; campaigns.</p>
        </div>
      </div>
      <div className="relative group w-full">
        <div className="flex gap-4 w-max animate-scroll group-hover:[animation-play-state:paused] px-4">
          {loop.map((reel, i) => (
            <button
              key={`${reel.id}-${i}`}
              onClick={() => onOpen(reel)}
              className="relative flex-shrink-0 w-[160px] sm:w-[200px] aspect-[9/16] rounded-2xl overflow-hidden shadow-lg border-2 border-slate-100 hover:border-blue-400 transition-all hover:scale-[1.03]"
            >
              {reel.thumbnail ? (
                <img src={reel.thumbnail} alt={reel.caption || 'Reel'} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  <Play className="w-5 h-5 text-blue-600 ml-0.5" fill="currentColor" />
                </div>
              </div>
              {reel.caption && <p className="absolute bottom-2 left-2 right-2 text-white text-xs font-semibold text-left line-clamp-2">{reel.caption}</p>}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

// Convert a YouTube/Instagram/mp4 url into an embeddable src for the reel modal
const toEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:shorts\/|embed\/|v\/|watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (yt) return { type: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0&playsinline=1` };
  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)) return { type: 'video', src: url };
  return { type: 'link', src: url };
};

const Landing = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [user, setUser] = useState(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [trustBadges, setTrustBadges] = useState([]);
  const [welcomeVideoId, setWelcomeVideoId] = useState('aMKXcXAz9N4');
  const [introVideoId, setIntroVideoId] = useState('aMKXcXAz9N4');
  const [reels, setReels] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeReel, setActiveReel] = useState(null);
  const [activationOpen, setActivationOpen] = useState(false);
  const [activationFee, setActivationFee] = useState(9);
  const openActivation = () => {
    setShowWelcomePopup(false);
    setMobileMenuOpen(false);
    // Already-activated clients go straight to their dashboard
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      if (u && (u.isActivated || u.activePlan)) { navigate('/dashboard'); return; }
    } catch (_e) { /* ignore */ }
    setActivationOpen(true);
    try { fbTrackLead(); } catch (_e) {}
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));

    // Fetch trust badges
    fetchTrustBadges();
    // Fetch reels & reviews (admin managed)
    (async () => {
      try { const r = await axios.get(`${API}/reels`); if (Array.isArray(r.data)) setReels(r.data); } catch (_e) { /* ignore */ }
      try { const rv = await axios.get(`${API}/reviews`); if (Array.isArray(rv.data)) setReviews(rv.data); } catch (_e) { /* ignore */ }
      try { const pi = await axios.get(`${API}/plans-info`); if (pi.data?.activationFee) setActivationFee(pi.data.activationFee); } catch (_e) { /* ignore */ }
    })();
    // Fetch admin-configurable videos + popup toggle, then schedule the welcome popup
    (async () => {
      let popupEnabled = true;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/site-videos`);
        if (res.data?.welcomeVideoId) setWelcomeVideoId(res.data.welcomeVideoId);
        if (res.data?.introVideoId) setIntroVideoId(res.data.introVideoId);
        if (res.data?.welcomePopupEnabled === false) popupEnabled = false;
      } catch (_e) { /* keep defaults */ }

      const popupShown = sessionStorage.getItem('welcomePopupShown');
      if (popupEnabled && !popupShown) {
        const isMobile = window.innerWidth < 768;
        const delay = isMobile ? 9000 : 5000;
        setTimeout(() => {
          setShowWelcomePopup(true);
          sessionStorage.setItem('welcomePopupShown', 'true');
        }, delay);
      }
    })();
  }, []);
  
  const fetchTrustBadges = async () => {
    try {
      const response = await axios.get(`${API}/trust-badges`);
      setTrustBadges(response.data);
    } catch (error) {
      console.error('Error fetching trust badges:', error);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleStartDropshipping = () => {
    setShowWelcomePopup(false);
    // New funnel: Get Started → ₹9 Activation (Cashfree) → dashboard
    openActivation();
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setLoginModalOpen(false);
    if (selectedPlan) navigate('/payment', { state: { plan: selectedPlan } });
    else navigate('/dashboard');
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      await axios.post(`${API}/contact-requests`, contactForm);
      // FB Pixel: Lead event
      try {
        fbTrackLead({ content_name: 'Landing Page Contact Form' });
      } catch (_e) { /* silent */ }
      alert('Message sent successfully! We will contact you soon.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      alert('Failed to send message. Please try again.');
    }
    setContactLoading(false);
  };

  // Features
  const features = [
    { title: 'Winning Products', description: 'Curated high-margin products from verified Indian suppliers', icon: Package, color: 'from-blue-500 to-blue-500' },
    { title: 'Direct Sourcing', description: 'Direct manufacturer access for best wholesale prices', icon: Truck, color: 'from-blue-600 to-blue-500' },
    { title: 'Expert Ads Setup', description: 'Professional Facebook & Instagram ad campaigns', icon: Target, color: 'from-blue-400 to-blue-500' },
    { title: '24/7 Support', description: 'Dedicated WhatsApp support for all queries', icon: Headphones, color: 'from-blue-500 to-blue-600' },
    { title: 'Complete Dashboard', description: 'Track orders, sales, and analytics real-time', icon: BarChart3, color: 'from-blue-500 to-blue-500' },
    { title: 'Fast Delivery', description: 'Pan-India delivery with trusted partners', icon: Zap, color: 'from-blue-400 to-cyan-400' }
  ];

  // Workflow steps
  const workflowSteps = [
    { step: 1, title: 'Choose Package', description: 'Select plan', icon: Package },
    { step: 2, title: 'WhatsApp Setup', description: 'Connect team', icon: Phone },
    { step: 3, title: 'Store Creation', description: 'Shopify store', icon: Globe },
    { step: 4, title: 'Product Selection', description: 'Pick winners', icon: ShoppingCart },
    { step: 5, title: 'Ads Launch', description: 'Go live', icon: Target },
    { step: 6, title: 'Start Earning', description: 'Get profits', icon: TrendingUp }
  ];

  // Pricing Plans
  // Dashboard Screenshots
  // Trust bar badges (different from admin-managed trust badges)
  const trustBarBadges = [
    { icon: Shield, title: '100% Secure', desc: 'Safe payments' },
    { icon: Award, title: 'Verified', desc: 'Quality assured' },
    { icon: Clock, title: 'Fast Setup', desc: '24-48 hours' },
    { icon: Users, title: '10K+ Clients', desc: 'Trusted' },
    { icon: BadgeCheck, title: 'Money Back', desc: '7-day refund' },
    { icon: Lock, title: 'SSL Secure', desc: 'Encrypted' }
  ];

  // Stats
  const stats = [
    { value: 10000, suffix: '+', label: 'Happy Clients', icon: Users },
    { value: 50, prefix: '₹', suffix: 'L+', label: 'Monthly Sales', icon: TrendingUp },
    { value: 500, suffix: '+', label: 'Products', icon: Package },
    { value: 98, suffix: '%', label: 'Success Rate', icon: Award }
  ];

  // Payment methods
  const paymentMethods = [
    { name: 'GPay', icon: '💳' },
    { name: 'PhonePe', icon: '📱' },
    { name: 'Paytm', icon: '💰' },
    { name: 'UPI', icon: '🏦' },
    { name: 'Bank Transfer', icon: '🏛️' }
  ];

  // Shipping partners
  const shippingPartners = ['Delhivery', 'Blue Dart', 'DTDC', 'Ekart', 'Shadowfax'];

  const faqs = [
    { question: 'What is DropSquadz?', answer: 'DropSquadz is India\'s leading dropshipping platform providing complete solutions - store setup, products, and ad campaigns.' },
    { question: 'How many products do I get?', answer: 'Basic: 1 product, Advanced: 2 products, Professional: 5 products with varying profit margins.' },
    { question: 'What is the minimum ads budget?', answer: 'We recommend minimum ₹500/day per product. Advanced & Professional plans include free ads credits.' },
    { question: 'Do I need prior experience?', answer: 'Not at all! We provide complete guidance. Our team handles everything from setup to ad management.' },
    { question: 'How long does setup take?', answer: 'Your complete store is ready within 3-5 business days after payment and onboarding.' },
    { question: 'Is there a refund policy?', answer: 'Yes! 7-day money-back guarantee. Full refund if not satisfied within 7 days.' }
  ];

  const testimonials = [
    { name: 'Rahul Sharma', location: 'Delhi', text: 'Made ₹35,000 profit in first month! Amazing support team. Now on Professional plan.', rating: 5, image: 'https://randomuser.me/api/portraits/men/1.jpg', revenue: '₹35K/month' },
    { name: 'Priya Patel', location: 'Mumbai', text: 'Best decision! Ads setup is professional and converts well. ROI in 2 weeks!', rating: 5, image: 'https://randomuser.me/api/portraits/women/2.jpg', revenue: '₹50K/month' },
    { name: 'Amit Kumar', location: 'Bangalore', text: 'Zero to ₹2L monthly in 3 months. Team guided me through everything!', rating: 5, image: 'https://randomuser.me/api/portraits/men/3.jpg', revenue: '₹2L/month' },
    { name: 'Sneha Reddy', location: 'Hyderabad', text: 'Excellent product quality and margins. Support always available on WhatsApp!', rating: 5, image: 'https://randomuser.me/api/portraits/women/4.jpg', revenue: '₹45K/month' },
    { name: 'Vikram Singh', location: 'Jaipur', text: 'Finally a reliable partner. No fake promises, real results!', rating: 5, image: 'https://randomuser.me/api/portraits/men/5.jpg', revenue: '₹60K/month' },
    { name: 'Anita Desai', location: 'Pune', text: 'Dashboard is so easy! I track everything - orders, payments, ads easily.', rating: 5, image: 'https://randomuser.me/api/portraits/women/6.jpg', revenue: '₹40K/month' }
  ];

  // Prefer admin-managed reviews; fall back to the built-in ones
  const displayReviews = reviews.length > 0 ? reviews : testimonials;

  return (
    <div className="min-h-screen bg-white text-slate-800 overflow-x-clip pb-20 md:pb-0">
      {/* Welcome Popup */}
      <Dialog open={showWelcomePopup && !loginModalOpen} onOpenChange={setShowWelcomePopup}>
        <DialogContent className="bg-white border-2 border-blue-200 text-slate-800 max-w-lg p-0 overflow-hidden shadow-2xl z-[90] w-[calc(100vw-24px)] sm:w-full [&>button.opacity-70]:hidden">
          {/* Custom visible close button */}
          <button
            type="button"
            aria-label="Close"
            onClick={() => setShowWelcomePopup(false)}
            className="absolute top-3 right-3 z-50 w-9 h-9 rounded-full bg-black/70 hover:bg-black/90 text-white shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95 ring-2 ring-white"
            data-testid="welcome-popup-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="relative">
            <div className="aspect-[9/16] max-h-[65vh] w-full bg-slate-100">
              <iframe
                src={`https://www.youtube.com/embed/${welcomeVideoId}?autoplay=1&mute=0&playsinline=1&rel=0`}
                title="DropSquadz Welcome"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="p-4 sm:p-5 bg-white">
              <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-2 text-center">🚀 Welcome to DropSquadz!</h3>
              <p className="text-slate-500 text-sm text-center mb-4">Start your dropshipping journey today</p>
              <Button onClick={handleStartDropshipping} className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white py-4 sm:py-5 text-sm sm:text-lg font-bold transition-all hover:scale-105 shadow-lg shadow-blue-500/30 px-3">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-yellow-300 animate-pulse shrink-0" />
                <span className="whitespace-nowrap">Start Your Store Now</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-sm">
        {/* Offer Campaign Banner (sticky inside header) — hidden on mobile */}
        <div className="hidden md:block">
          <OfferBanner onClaim={handleStartDropshipping} />
        </div>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/dropsquadz-logo.jpeg" alt="DropSquadz" className="w-10 h-10 rounded-full object-cover ring-2 ring-blue-500" />
              <span className="text-xl font-bold text-blue-600">DropSquadz</span>
            </div>

            <nav className="hidden lg:flex items-center space-x-1">
              {['Why Us', 'How It Works', 'Results', 'Reviews', 'FAQ', 'Contact'].map((item) => (
                <button key={item} onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))} className="px-4 py-2 rounded-lg hover:bg-blue-50 transition-all text-sm font-medium text-slate-600 hover:text-blue-600">
                  {item}
                </button>
              ))}
            </nav>

            <div className="hidden md:flex items-center space-x-3">
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700 text-white">Dashboard</Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => { setShowWelcomePopup(false); setLoginModalOpen(true); }} className="hover:bg-blue-50 text-slate-600">Log In</Button>
                  <Button onClick={handleStartDropshipping} data-testid="navbar-get-started-desktop" className="bg-blue-600 hover:bg-blue-700 text-white">Get Started</Button>
                </>
              )}
            </div>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-slate-800">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-2 border-t border-slate-200 pt-4">
              {['Why Us', 'How It Works', 'Results', 'Reviews', 'FAQ', 'Contact'].map((item) => (
                <button key={item} onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))} className="block w-full text-left px-4 py-2 hover:bg-blue-50 rounded-lg text-slate-600">
                  {item}
                </button>
              ))}
              <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-slate-100">
                <Button
                  variant="outline"
                  onClick={() => { setMobileMenuOpen(false); setShowWelcomePopup(false); setLoginModalOpen(true); }}
                  data-testid="navbar-login-mobile"
                  className="border-blue-200 text-blue-600 hover:bg-blue-50"
                >
                  Log In
                </Button>
                <Button onClick={() => { setMobileMenuOpen(false); handleStartDropshipping(); }} data-testid="navbar-get-started-mobile" className="bg-blue-600 hover:bg-blue-700 text-white">Get Started</Button>
              </div>
              <p className="text-xs text-slate-400 text-center pt-2">
                Already a client? Tap <span className="font-semibold text-blue-600">Log In</span>
              </p>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-28 pb-8 md:pt-36 md:pb-12 px-4 relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <WaveBackground />

        <div className="container mx-auto relative z-10">
          {/* Hero Text Content - Centered */}
          <div className="text-center max-w-4xl mx-auto mb-12">
            <AnimatedSection>
              <div className="inline-flex items-center px-4 py-2 bg-blue-100 border border-blue-200 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-blue-600 mr-2 animate-pulse" />
                <span className="text-blue-700 text-sm font-medium">India's #1 Dropshipping Platform</span>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={100}>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                <span className="text-slate-800">Start Your</span>
                <br />
                <span className="text-blue-600">
                  <TypingEffect texts={['Dropshipping Business', 'E-commerce Empire', 'Online Store']} />
                </span>
              </h1>
            </AnimatedSection>
            
            <AnimatedSection delay={200}>
              <p className="text-base sm:text-lg text-slate-600 mb-6 max-w-2xl mx-auto">
                Complete setup with winning products, Shopify store & Facebook ads. 
                <span className="text-blue-600 font-semibold"> Earn from home!</span>
              </p>
            </AnimatedSection>

            <AnimatedSection delay={300}>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
                <Button size="lg" onClick={handleStartDropshipping} className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg shadow-xl shadow-blue-500/30 hover:scale-105 active:scale-95 transition-all group font-bold rounded-xl">
                  <Sparkles className="w-5 h-5 mr-2 text-yellow-300 animate-pulse" />
                  <span>Start Your Store</span>
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => scrollToSection('results')} className="w-full sm:w-auto border-2 border-blue-500 text-blue-600 hover:bg-blue-50 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-xl">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  See Real Results
                </Button>
              </div>
              <p className="text-[11px] sm:text-sm text-slate-500 mb-2">
                🚀 Sign up in minutes • Choose your store • Pick winning products • Go live
              </p>
            </AnimatedSection>

            <AnimatedSection delay={350}>
              <div className="flex justify-center mb-2">
                <SeatsBadge />
              </div>
            </AnimatedSection>

            <AnimatedSection delay={400}>
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs sm:text-sm text-slate-600">
                <div className="flex items-center gap-1.5"><Users className="w-4 h-4 text-blue-500" /><span><span className="font-bold text-slate-800">5,415+</span> Clients</span></div>
                <span className="text-slate-300">•</span>
                <div className="flex items-center gap-1.5"><Star className="w-4 h-4 text-yellow-500 fill-yellow-500" /><span><span className="font-bold text-slate-800">4.8/5</span> Rating</span></div>
                <span className="text-slate-300">•</span>
                <div className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-green-500" /><span>100% Secure</span></div>
                <span className="text-slate-300">•</span>
                <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-blue-500" /><span>24-48hr Setup</span></div>
                <span className="text-slate-300">•</span>
                <div className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-blue-500" /><span>7-Day Refund</span></div>
              </div>
            </AnimatedSection>
          </div>

          {/* Stats */}
          <AnimatedSection delay={500}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="p-4 md:p-6 bg-white border border-slate-200 rounded-2xl shadow-lg hover:shadow-xl transition-all group hover:scale-105 hover:border-blue-300 text-center">
                    <Icon className="w-6 h-6 md:w-8 md:h-8 text-blue-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                    <div className="text-xl md:text-2xl lg:text-3xl font-bold text-blue-600">
                      <AnimatedCounter prefix={stat.prefix} end={stat.value} suffix={stat.suffix} />
                    </div>
                    <div className="text-slate-500 text-xs md:text-sm">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Introduction Video Section - Below Hero */}
      <section id="intro-video" className="py-16 md:py-20 px-4 bg-white">
        <div className="container mx-auto">
          <AnimatedSection>
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-2 bg-blue-100 border border-blue-200 rounded-full text-blue-600 text-sm font-medium mb-4">🎬 Introduction Video</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mb-4">Watch How It <span className="text-blue-600">Works</span></h2>
              <p className="text-slate-600 max-w-2xl mx-auto">Learn how DropSquadz can help you start your profitable dropshipping business</p>
            </div>
          </AnimatedSection>

          <div className="grid lg:grid-cols-2 gap-8 items-center max-w-6xl mx-auto">
            {/* Video */}
            <AnimatedSection delay={100}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-blue-200 bg-white group">
                <div className="aspect-video">
                  <iframe src={`https://www.youtube.com/embed/${introVideoId}`} title="Introduction Video" className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              </div>
            </AnimatedSection>
            
            {/* Earnings Calculator */}
            <AnimatedSection delay={200}>
              <EarningsCalculator />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Reels Carousel (admin-managed) — right below the Intro Video + Calculator */}
      <ReelsCarousel reels={reels} onOpen={setActiveReel} />

      {/* NEW FUNNEL: What you get + Earnings by plan */}
      <WhatYouGet onActivate={openActivation} activationFee={activationFee} />
      <SeatsBar onClick={openActivation} />
      <PlanEarnings onActivate={openActivation} />


      {/* Trust Bar */}
      <section className="py-4 bg-blue-600 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-3 gap-x-2 gap-y-3 md:flex md:flex-wrap md:justify-center md:items-center md:gap-8">
            {trustBarBadges.map((badge, index) => {
              const Icon = badge.icon;
              return (
                <div key={index} className="flex items-center space-x-1.5 md:space-x-2 text-white min-w-0">
                  <Icon className="w-4 h-4 md:w-5 md:h-5" />
                  <div>
                    <div className="font-semibold text-xs md:text-sm">{badge.title}</div>
                    <div className="text-[10px] md:text-xs text-blue-100">{badge.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-us" className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="container mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-blue-100 border border-blue-200 rounded-full text-blue-600 text-sm font-medium mb-4">✨ Why Choose Us</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">Everything You Need to <span className="text-blue-600">Succeed</span></h2>
            </div>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <AnimatedSection key={index} delay={index * 100}>
                  <Card className="bg-white border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all group hover:scale-105 hover:-translate-y-1 h-full">
                    <CardContent className="p-6">
                      <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all shadow-lg`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-800 mb-2">{feature.title}</h3>
                      <p className="text-slate-500 text-sm">{feature.description}</p>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 md:py-24 px-4 bg-white">
        <div className="container mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-blue-100 border border-blue-200 rounded-full text-blue-600 text-sm font-medium mb-4">📋 Simple Process</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">How It <span className="text-blue-600">Works</span></h2>
            </div>
          </AnimatedSection>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {workflowSteps.map((item, index) => {
                const Icon = item.icon;
                return (
                  <AnimatedSection key={index} delay={index * 100}>
                    <div className="flex flex-col items-center text-center group">
                      <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-3 shadow-lg shadow-blue-500/30 group-hover:scale-110 group-hover:rotate-6 transition-all relative">
                        <Icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
                        <span className="absolute -top-2 -right-2 w-6 h-6 bg-white text-blue-600 rounded-full flex items-center justify-center font-bold text-xs shadow-lg border-2 border-blue-600">{item.step}</span>
                      </div>
                      <h3 className="text-sm md:text-base font-bold text-slate-800 mb-1">{item.title}</h3>
                      <p className="text-slate-500 text-xs">{item.description}</p>
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Website Showcase (plan-wise, admin-managed) — replaces old dashboard screenshots section */}
      <WebsiteShowcase onActivate={openActivation} />

      {/* Real Results Showcase — build trust & boost conversion */}
      <div id="results">
        <LiveSuccessMetrics />
      </div>


      {/* Testimonials */}
      <section id="reviews" className="py-16 md:py-24 px-4 bg-white">
        <div className="container mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-blue-100 border border-blue-200 rounded-full text-blue-600 text-sm font-medium mb-4">⭐ Client Reviews</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">What Our <span className="text-blue-600">Clients Say</span></h2>
            </div>
          </AnimatedSection>

          {/* Testimonial Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
            {displayReviews.map((testimonial, index) => (
              <AnimatedSection key={testimonial.id || index} delay={index * 100}>
                <Card className="bg-white border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all h-full group hover:scale-105 relative overflow-hidden">
                  <CardContent className="p-5">
                    {/* Verified Badge */}
                    <div className="absolute top-3 right-3">
                      <div className="flex items-center space-x-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-bold">
                        <Verified className="w-3 h-3" />
                        <span>Verified</span>
                      </div>
                    </div>

                    {/* Name, Photo and Location */}
                    <div className="mb-4 pt-2">
                      <div className="flex items-center gap-3 mb-1">
                        {testimonial.image && (
                          <img src={testimonial.image} alt={testimonial.name} className="w-11 h-11 rounded-full object-cover border-2 border-blue-100" onError={(e) => { e.target.style.display = 'none'; }} />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-bold text-slate-800 text-base truncate">{testimonial.name}</p>
                            {testimonial.revenue && <div className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-bold whitespace-nowrap">{testimonial.revenue}</div>}
                          </div>
                          <p className="text-xs text-slate-500 flex items-center">
                            <MapPin className="w-3 h-3 mr-1" />
                            {testimonial.location}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="flex mb-3">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400" fill="#facc15" />
                      ))}
                    </div>

                    {/* Quote Icon and Text */}
                    <div className="relative">
                      <Quote className="w-6 h-6 text-blue-200 absolute -top-1 -left-1" />
                      <p className="text-slate-600 text-sm italic pl-5">"{testimonial.text}"</p>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Certifications */}
      {trustBadges.length > 0 && (
        <section className="py-16 md:py-24 px-4 bg-white">
          <div className="container mx-auto max-w-6xl">
            <AnimatedSection>
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-2 bg-green-100 border border-green-200 rounded-full text-green-600 text-sm font-medium mb-4">
                  🏆 Verified & Trusted
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800">
                  Our <span className="text-blue-600">Certifications</span> & Trust Badges
                </h2>
                <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
                  Certified, verified, and trusted by industry leaders
                </p>
              </div>
            </AnimatedSection>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {trustBadges.map((badge, index) => (
                <AnimatedSection key={badge.badgeId} delay={index * 0.1}>
                  <Card className="bg-white border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all text-center group">
                    <CardContent className="p-6">
                      <div className="mb-4 flex justify-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          {badge.imageUrl ? (
                            <img
                              src={badge.imageUrl}
                              alt={badge.title}
                              className="w-16 h-16 object-contain"
                            />
                          ) : (
                            <Award className="w-10 h-10 text-blue-600" />
                          )}
                        </div>
                      </div>
                      <h3 className="font-bold text-slate-800 mb-2">{badge.title}</h3>
                      <p className="text-xs text-slate-600 mb-2">{badge.description}</p>
                      {badge.issuedBy && (
                        <p className="text-xs text-blue-600 font-medium">
                          by {badge.issuedBy}
                        </p>
                      )}
                      {badge.issuedDate && (
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(badge.issuedDate).getFullYear()}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </AnimatedSection>
              ))}
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-8 h-8 text-green-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-1">100% Secure</h4>
                <p className="text-xs text-slate-600">SSL Encrypted</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <BadgeCheck className="w-8 h-8 text-blue-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-1">Verified</h4>
                <p className="text-xs text-slate-600">Authentic Business</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Headphones className="w-8 h-8 text-purple-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-1">24/7 Support</h4>
                <p className="text-xs text-slate-600">Always Available</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <RefreshCw className="w-8 h-8 text-orange-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-1">Money Back</h4>
                <p className="text-xs text-slate-600">30-Day Guarantee</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section id="faq" className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="container mx-auto max-w-3xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-blue-100 border border-blue-200 rounded-full text-blue-600 text-sm font-medium mb-4">❓ FAQ</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800">Frequently Asked <span className="text-blue-600">Questions</span></h2>
            </div>
          </AnimatedSection>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <AnimatedSection key={index} delay={index * 50}>
                <Card className={`bg-white border-slate-200 hover:border-blue-300 transition-all cursor-pointer ${expandedFAQ === index ? 'ring-2 ring-blue-500/50 shadow-lg' : 'shadow'}`} onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}>
                  <CardHeader className="p-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm md:text-base text-slate-800 pr-4">{faq.question}</CardTitle>
                      {expandedFAQ === index ? <ChevronUp className="text-blue-500 w-5 h-5" /> : <ChevronDown className="text-slate-400 w-5 h-5" />}
                    </div>
                  </CardHeader>
                  {expandedFAQ === index && (
                    <CardContent className="px-4 pb-4 pt-0">
                      <p className="text-slate-600 text-sm">{faq.answer}</p>
                    </CardContent>
                  )}
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-16 md:py-24 px-4 bg-white">
        <div className="container mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-blue-100 border border-blue-200 rounded-full text-blue-600 text-sm font-medium mb-4">📞 Contact Us</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800">Get In <span className="text-blue-600">Touch</span></h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <AnimatedSection>
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-slate-800 mb-6">Contact Information</h3>
                {[
                  { icon: Mail, title: 'Email', value: 'support@dropsquadz.tech' },
                  { icon: Instagram, title: 'Instagram', value: '@dropsquadz', link: 'https://instagram.com/dropsquadz' },
                  { icon: MapPin, title: 'Address', value: 'Shahdol, MP 484001' }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-center space-x-4 p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-300 transition-all">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-slate-500 text-sm">{item.title}</p>
                        {item.link ? (
                          <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-slate-800 font-medium hover:text-blue-600">{item.value}</a>
                        ) : (
                          <p className="text-slate-800 font-medium">{item.value}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <Card className="bg-white border-slate-200 shadow-lg p-5">
                <h3 className="text-xl font-bold text-slate-800 mb-4">Send Message</h3>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  {['name', 'email', 'phone'].map((field) => (
                    <input key={field} type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'} placeholder={field.charAt(0).toUpperCase() + field.slice(1) + ' *'} value={contactForm[field]} onChange={(e) => setContactForm({ ...contactForm, [field]: e.target.value })} required className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors text-sm" />
                  ))}
                  <textarea placeholder="Message *" value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} required rows={3} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors resize-none text-sm" />
                  <Button type="submit" disabled={contactLoading} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 font-semibold transition-all hover:scale-105">
                    {contactLoading ? 'Sending...' : <><Send className="w-4 h-4 mr-2" />Send Message</>}
                  </Button>
                </form>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-4 bg-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.05)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.05)_50%,rgba(255,255,255,0.05)_75%,transparent_75%)] bg-[size:60px_60px]" />
        <div className="container mx-auto text-center relative z-10">
          <AnimatedSection>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">Ready to Start Earning?</h2>
            <p className="text-blue-100 text-base md:text-lg mb-2 max-w-xl mx-auto">Join 10,000+ entrepreneurs already earning with DropSquadz</p>
            <p className="text-yellow-300 text-sm md:text-base font-bold mb-6">🎉 No plans, no confusion — pick a store, pick products, go live</p>
            <Button onClick={handleStartDropshipping} size="lg" className="bg-white text-blue-600 hover:bg-slate-100 px-6 sm:px-8 py-5 text-base sm:text-lg font-bold shadow-xl transition-all hover:scale-105 active:scale-95 rounded-xl">
              <Sparkles className="w-5 h-5 mr-2 text-orange-500" />
              Start Your Store
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-200 bg-white">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <img src="/dropsquadz-logo.jpeg" alt="DropSquadz" className="w-10 h-10 rounded-full ring-2 ring-blue-500" />
              <span className="text-xl font-bold text-blue-600">DropSquadz</span>
            </div>
            <p className="text-slate-500 text-sm">&copy; 2025 DropSquadz. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-3 bg-white/95 backdrop-blur-xl border-t border-slate-200 md:hidden z-40 safe-area-inset-bottom shadow-[0_-8px_24px_rgba(0,0,0,0.08)]" data-testid="sticky-mobile-cta">
        <Button onClick={handleStartDropshipping} className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white py-4 text-base font-bold shadow-lg active:scale-95 transition-all rounded-xl">
          <Rocket className="w-5 h-5 mr-2" />
          <span className="flex flex-col items-start leading-tight">
            <span>Start Your Store</span>
            <span className="text-[10px] font-normal text-blue-100">Winning products · Live in 24-48hr</span>
          </span>
          <ArrowRight className="w-5 h-5 ml-auto" />
        </Button>
      </div>

      {/* Login Modal */}
      {loginModalOpen && <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} onLoginSuccess={handleLoginSuccess} />}
      <ActivationModal open={activationOpen} onClose={() => setActivationOpen(false)} activationFee={activationFee} defaults={user ? { name: user.name || '', email: user.email || '', phone: user.phone || '' } : undefined}
        onActivated={(verifyData) => {
          if (verifyData?.user) {
            // already logged in via authUtils in ActivationModal
          }
          navigate('/dashboard', { replace: true });
        }}
      />

      {/* Reel Player Modal */}
      {activeReel && (() => {
        const embed = toEmbed(activeReel.videoUrl);
        return (
          <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" onClick={() => setActiveReel(null)}>
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setActiveReel(null)} aria-label="Close" className="absolute -top-11 right-0 w-9 h-9 rounded-full bg-white/90 text-slate-800 flex items-center justify-center shadow-lg hover:scale-110 transition-all">
                <X className="w-5 h-5" />
              </button>
              <div className="aspect-[9/16] w-full rounded-2xl overflow-hidden bg-black shadow-2xl">
                {embed?.type === 'iframe' && (
                  <iframe src={embed.src} title={activeReel.caption || 'Reel'} className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                )}
                {embed?.type === 'video' && (
                  <video src={embed.src} className="w-full h-full object-contain" controls autoPlay playsInline />
                )}
                {embed?.type === 'link' && (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-4 text-center">
                    {activeReel.thumbnail && <img src={activeReel.thumbnail} alt="reel" className="max-h-2/3 rounded-xl" />}
                    <a href={embed.src} target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-semibold">Watch Reel</a>
                  </div>
                )}
              </div>
              {activeReel.caption && <p className="text-white text-center text-sm mt-3">{activeReel.caption}</p>}
            </div>
          </div>
        );
      })()}

      {/* WhatsApp Button - visible on all screens (positioned above sticky CTA on mobile) */}
      <WhatsAppButton />

      {/* Animations CSS */}
      <style jsx>{`
        @keyframes blob { 0%, 100% { transform: translate(0, 0) scale(1); } 33% { transform: translate(30px, -50px) scale(1.1); } 66% { transform: translate(-20px, 20px) scale(0.9); } }
        @keyframes float { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-20px) rotate(5deg); } }
        @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-scroll { animation: scroll 20s linear infinite; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        input[type="range"]::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 20px; height: 20px; border-radius: 50%; background: white; cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,0.2); }
        input[type="range"]::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; background: white; cursor: pointer; border: none; box-shadow: 0 2px 6px rgba(0,0,0,0.2); }
      `}</style>
    </div>
  );
};

export default Landing;
